import * as Application from 'expo-application';
import * as Updates from 'expo-updates';
import Constants from 'expo-constants';
import {
  ErrorReporter,
  sentryIntegrations,
  sentryNavigationIntegration,
} from './platform';
import { partnerSupplier, runtimeEnvironment } from '@/utils/environment';
import { Platform } from 'react-native';
import { sentryTracingRuntimeConfig } from './runtime-config';

export { ErrorReporter, sentryNavigationIntegration };

export const initializeSentry = () => {
  const version = Constants.expoConfig?.version;
  const slug = Constants.expoConfig?.slug;
  const nativeBuildId = Application.nativeBuildVersion;
  // Determine the dist value based on platform:
  // - Web: Only one deployed service. use 'web-{environment}' to match next.config.js sourcemap upload
  // - Native: Use Updates.channel (e.g., 'springfield-fine-foods') to match CI/CD sourcemap upload
  //           Updates.channel could be null if the new build has no OTA updates yet. In this case
  //           use the native build id to match the expo build behavior.
  const dist =
    Platform.OS === 'web'
      ? `web-${process.env.NEXT_ENV || 'unknown'}`
      : Updates.channel || nativeBuildId;

  // Per-OTA release id so each EAS Update gets its own sourcemap. Falls back to native build id pre-OTA. Format `jalappeno@<version>+<bundle_id>` must match deploy_app.yml sourcemap upload or symbolication silently fails.
  const bundleId = Updates.updateId ?? nativeBuildId;
  const release = bundleId
    ? `jalappeno@${version}+${bundleId}`
    : `jalappeno@${version}`;

  ErrorReporter.init({
    ...Constants.expoConfig!.extra!.sentry,
    // Attach the view hierarchy (component tree structure, no user content) to error reports — useful for debugging without leaking PII.
    attachViewHierarchy: true,
    // Prevent non-actionable errors from being sent to ErrorReporter
    beforeBreadcrumb: (breadcrumbInfo) => {
      // including all console warnings and errors in breadcrumbs can lead to
      // request payloads that are too large for ErrorReporter, so only include
      // console errors
      if (
        breadcrumbInfo.category === 'console' &&
        breadcrumbInfo.level !== 'error'
      ) {
        return null;
      }
      return breadcrumbInfo;
    },
    beforeSend: (event) => {
      // some errors report to sentry despite enableInExpoDevelopment being false
      // so manually ignore errors here.
      return __DEV__ ? null : event;
    },
    beforeSendTransaction: (event) => {
      // Drop low-value child spans from web PerformanceObserver (resource loads, paint marks, user timing). Transaction measurements (LCP/FCP/CLS/TTFB) and http/graphql child spans are kept.
      if (event.spans) {
        event.spans = event.spans.filter((span) => {
          const op = span.op ?? '';
          return (
            !op.startsWith('resource.') &&
            !['browser', 'paint', 'mark', 'measure'].includes(op)
          );
        });
      }
      return event;
    },
    dist,
    enableInExpoDevelopment: false,
    enableUserInteractionTracing: true,
    environment: runtimeEnvironment,
    ignoreErrors: [
      'Broken pipe',
      // Thrown by use-save-notification hook
      'Error encountered while fetching Expo token',
      'Error writing to stream',
      'Failed to download manifest',
      'Failed to download new update',
      // Generic network request error
      'A network error occurred',
      'JWTExpired',
      'Failed to fetch',
      'Invalid session for the user',
      'Load failed',
      'Network error',
      'Network is down',
      'Network request failed',
      'No current user',
      // "The operation couldn't be completed" error with websockets on iOS
      'OSStatus error -9806',
      'Refresh Token has expired',
      'The Internet connection appears to be offline',
      'A server with the specified hostname could not be found',
      'signal is aborted without reason',
      // apollo errors to look into after version bump
      // https://usepepper.atlassian.net/browse/PROD-720
      'start received before the connection is initialised',
      /server returned results with length 1, expected length of \d+/,
      'Socket is not connected',
    ],
    integrations: sentryIntegrations,
    release,
    // Anchored to https:// + hostname end so we don't propagate to URLs that only mention the string (query params, etc).
    tracePropagationTargets: [/^https:\/\/[^/]+\.usepepper\.com\//],
    tracesSampler: () => {
      if (runtimeEnvironment !== 'production') {
        return 0;
      }
      const { enabled, sampleRate } = sentryTracingRuntimeConfig.get();
      return enabled ? sampleRate : 0;
    },
  });

  if (partnerSupplier) {
    ErrorReporter.setTag('app.name', partnerSupplier.fullName);
  }
  if (slug) {
    ErrorReporter.setTag('app.slug', slug);
  }
  if (version) {
    ErrorReporter.setTag('app.version', version);
  }
  if (Updates.updateId) {
    ErrorReporter.setTag('app.update_id', Updates.updateId);
  }
  ErrorReporter.setTag('app.update_channel', Updates.channel ?? 'embedded');
};

export const captureException: (
  ...args: Parameters<typeof ErrorReporter.captureException>
) => void = (error, context) => {
  if (process.env.NODE_ENV === 'test') {
    return;
  }

  console.error(error, context);
  ErrorReporter.captureException(error, context);
};
